import { lazy, Suspense, useEffect, useState, useMemo } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import Header, { getInitialDarkMode } from "./components/Header";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import seoData from "./components/seoData";
import gdAdMobile from "./legacy/assets/gd-ad-mobile-Bp6nztJd.jpg";
import Home from "./pages/Home";
const SearchResults = lazy(() => import("./pages/SearchResults"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const SuperDeals = lazy(() => import("./pages/SuperDeals"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const SuperDealsManager = lazy(() => import("./components/SuperDealsManager"));
import Keywords from "./components/keywords";
import BigKeywords from "./components/bigkeywords";
import {
  normalizeSuperDealsCategorySlug,
  superDealsCategoryLabelFromSlug,
} from "./utils/superDeals";
const TourPackagesPage = lazy(() => import("./pages/TourPackagesPage"));
const WebStory = lazy(() => import("./pages/WebStory"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Wishlistfavorites = lazy(() => import("./pages/Wishlistfavorites"));
const AdvertiseWithUs = lazy(() => import("./pages/AdvertiseWithUs"));
const Jobs = lazy(() => import("./pages/Jobs"));
const routerBasename =
  typeof import.meta !== "undefined"
    ? (import.meta.env.BASE_URL || "/").replace(/\/+$/, "") || "/"
    : "/";

const SEO_OVERRIDES_STORAGE_KEY = "trw-tour-seo-meta-v1";

function readTourPackagesSeoOverrides() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SEO_OVERRIDES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readSeoOverrides() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SEO_OVERRIDES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function mergeSeoOverride(baseSeo, overrideKey) {
  if (!baseSeo) return null;
  const override = readSeoOverrides()?.[overrideKey] || {};
  return {
    ...baseSeo,
    title: typeof override.title === "string" && override.title.trim() ? override.title.trim() : baseSeo.title,
    description: typeof override.description === "string" && override.description.trim() ? override.description.trim() : baseSeo.description,
  };
}

function getEmbeddedRoute() {
  if (typeof window === "undefined") {
    return null;
  }

  const route = window.__TRW_EMBED_ROUTE__;
  return typeof route === "string" && route.trim() ? route.trim() : null;
}

function splitRoute(route) {
  const [pathnamePart, searchPart = ""] = route.split("?");

  return {
    pathname: pathnamePart.startsWith("/") ? pathnamePart : `/${pathnamePart}`,
    search: searchPart ? `?${searchPart}` : "",
  };
}

function normalizeSeoPathname(pathname = "/") {
  const value = String(pathname || "").trim() || "/";
  if (value === "/") return "/";
  const normalized = value.replace(/\/+$/, "") || "/";
  switch (normalized) {
    case "/about-us":
      return "/about-us-travel-rethink-ways";
    case "/visual-stories":
      return "/travel-web-stories";
    case "/disclaimer-travel-rethink-ways":
      return "/disclaimer";
    case "/travel-tour-packages":
      return "/travel-tour-packages";
    case "/wishlist":
      return "/wishlist-favorites";
    case "/wishlist-favourites":
      return "/wishlist-favorites";
    default:
      return normalized;
  }
}

function getRootSearchQuery(search = "") {
  const params = new URLSearchParams(search);
  return params.get("q")?.trim() || params.get("search")?.trim() || params.get("s")?.trim() || "";
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure helper — resolves the correct SEO object for any route.
// Returns null for dynamic routes that manage their own SEO (e.g. /category/:slug).
// ─────────────────────────────────────────────────────────────────────────────
function resolveSeo(pathname, search) {
  const normalizedPathname = normalizeSeoPathname(pathname);
  const searchParams = new URLSearchParams(search);
  const legacySearchQuery = getRootSearchQuery(search);

  switch (normalizedPathname) {
    case "/":
      if (legacySearchQuery) {
        return mergeSeoOverride(seoData.searchResults, "site:searchResults");
      }
      return mergeSeoOverride(seoData.home, "site:home");

    case "/super-deals":
      {
        const catSlug = normalizeSuperDealsCategorySlug(searchParams.get("cat") || "");
        const catLabel = superDealsCategoryLabelFromSlug(catSlug);
        const seoEntry =
          (catLabel && seoData.superDeals.tags?.[catLabel]) ||
          (catSlug && seoData.superDeals.tags?.[catSlug]) ||
          seoData.superDeals;
        return mergeSeoOverride(seoEntry, catSlug ? "" : "site:superDeals");
      }

    case "/about-us-travel-rethink-ways":
      return mergeSeoOverride(seoData.about, "site:about");

    case "/contact-us":
      return mergeSeoOverride(seoData.contact, "site:contact");

    case "/privacy-policy":
      return mergeSeoOverride(seoData.privacyPolicy, "site:privacyPolicy");

    case "/cookies-policy":
      return mergeSeoOverride(seoData.cookiesPolicy, "site:cookiesPolicy");

    case "/terms-and-conditions":
      return mergeSeoOverride(seoData.termsConditions, "site:termsConditions");

    case "/affiliate-disclosure":
      return mergeSeoOverride(seoData.affiliate, "site:affiliate");

    case "/disclaimer":
      return mergeSeoOverride(seoData.disclaimer, "site:disclaimer");

    case "/latest-updates":
      return mergeSeoOverride(seoData.latestUpdates, "site:latestUpdates");

    case "/jobs":
    case "/jobs/":
      return mergeSeoOverride(seoData.jobs, "site:jobs");

    case "/search":
      return mergeSeoOverride(seoData.searchResults, "site:searchResults");

    case "/wishlist-favorites":
      return mergeSeoOverride(seoData.wishlist, "site:wishlist");

    case "/travel-web-stories":
      return mergeSeoOverride(seoData.webStory, "site:webStory");

    case "/tour-packages":
      {
        const sectionKey = String(searchParams.get("section") || "All").trim() || "All";
        const seoKey = `listing:${sectionKey.toLowerCase()}`;
        const baseSeo =
          seoData.tourPackages.tags?.[sectionKey] ||
          seoData.tourPackages.tags?.All ||
          seoData.tourPackages;
        const override = readTourPackagesSeoOverrides()?.[seoKey] || {};

        return {
          ...baseSeo,
          title: typeof override.title === "string" && override.title.trim() ? override.title.trim() : baseSeo.title,
          description: typeof override.description === "string" && override.description.trim() ? override.description.trim() : baseSeo.description,
        };
      }

    case "/advertise-with-us":
      return mergeSeoOverride(seoData.advertise, "site:advertise");

    // Dynamic routes handle their own SEO — return null so App never
    // overwrites their title with a generic fallback.
    default:
      return null;
  }
}

function getInitialLocation() {
  if (typeof window === "undefined") {
    return { pathname: "/", search: "" };
  }

  const embeddedRoute = getEmbeddedRoute();
  if (embeddedRoute) {
    return splitRoute(embeddedRoute);
  }

  const rawPathname = window.location.pathname || "/";
  const pathnamePart =
    routerBasename !== "/" && rawPathname.startsWith(routerBasename)
      ? rawPathname.slice(routerBasename.length) || "/"
      : rawPathname;

  return {
    pathname: pathnamePart.startsWith("/") ? pathnamePart : `/${pathnamePart}`,
    search: window.location.search,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Set <title> synchronously at module evaluation time — before React mounts,
// before the loading screen appears, before any paint. This is the single
// source of truth for the initial title and prevents any flash.
// ─────────────────────────────────────────────────────────────────────────────
const _initialLocation = getInitialLocation();
const _initialSeo = resolveSeo(_initialLocation.pathname, _initialLocation.search);
if (typeof document !== "undefined" && _initialSeo?.title) {
  document.title = _initialSeo.title;
}

// ─────────────────────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  const location     = useLocation();
  const legacySearchQuery = getRootSearchQuery(location.search);
  const isLegacySearchPage = location.pathname === "/" && Boolean(legacySearchQuery);
  const normalizedPathname = normalizeSeoPathname(location.pathname);
  const isCategoryPage = normalizedPathname.startsWith("/category/");
  const isTourDetailPage = /^\/(?:tour-packages\/)?[a-z0-9-]+-tour-packages\/?$/i.test(normalizedPathname);
  const isKeywordAllowedPage =
    normalizedPathname === "/" ||
    isLegacySearchPage ||
    normalizedPathname === "/latest-updates" ||
    normalizedPathname === "/search" ||
    normalizedPathname === "/super-deals" ||
    normalizedPathname === "/tour-packages" ||
    normalizedPathname === "/travel-tour-packages" ||
    isTourDetailPage ||
    normalizedPathname === "/travel-web-stories" ||
    isCategoryPage;

  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  // ── Resolve SEO once per navigation — memoised so it never re-runs
  //    mid-render and never falls back to a generic "Travel RethinkWays" title ─
  const seo = useMemo(
    () => resolveSeo(location.pathname, location.search),
    [location.pathname, location.search]
  );

  // Keep <title> in sync on every client-side navigation (including during boot)
  useEffect(() => {
    if (seo?.title) {
      document.title = seo.title;
    }
    // Do NOT set a fallback here — dynamic-route pages manage their own title
  }, [seo]);

  // ── Footer encoding fix ────────────────────────────────────────────────────
  useEffect(() => {
    const copyrightFixes = [
      "\u00c2\u00a9",
      "Ãƒâ€šÃ‚Â©",
      "ÃƒÆ'Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â©",
    ];

    const normalizeFooterText = () => {
      document.querySelectorAll("footer *").forEach((node) => {
        if (
          node.childNodes.length !== 1 ||
          node.firstChild?.nodeType !== Node.TEXT_NODE
        ) return;

        const text = node.textContent ?? "";

        if (copyrightFixes.some((v) => text.includes(v))) {
          node.textContent = copyrightFixes.reduce(
            (t, v) => t.replaceAll(v, "\u00a9"),
            text
          );
        }

        if (text.trim().toUpperCase() === "SPONSORED AD") {
          node.classList.remove("text-white", "text-white/45", "text-black/45", "opacity-100");
          node.classList.add("text-black");
          node.style.opacity = "1";
        }
      });
    };

    normalizeFooterText();
    const observer = new MutationObserver(normalizeFooterText);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // ── Analytics ──────────────────────────────────────────────────────────────
  useEffect(() => {
    window.gtag?.("config", "G-8RDF7WJVFH", { page_path: location.pathname });
    window.fbq?.("track", "PageView");
    window.clarity?.("set", "page", location.pathname);
  }, [location.pathname]);

  // Full-screen loader disabled.
  // (Previously showed a boot LoadingScreen + Suspense fallbacks.)
  // Removed to render pages immediately.

  // ── Full app ───────────────────────────────────────────────────────────────
  return (
    <div className="trw-app">
      {seo && (
        <SEO
          title={seo.title}
          description={seo.description}
          canonical={seo.canonical}
          image={seo.image}
          imageAlt={seo.imageAlt || seo.title}
          siteName="Travel Rethink Ways"
          locale="en_US"
          robots="index,follow,max-image-preview:large"
        />
      )}

      <Header darkMode={darkMode} onDarkModeChange={setDarkMode} />

      <div className="md:hidden">
        <div className="mt-2 mb-2 flex justify-center">
            <span className="text-[7px] tracking-[2px] text-black dark:text-white">
              SPONSORED AD
            </span>
          </div>
        <div className="mx-auto max-w-[1500px] px-4 py-4">
          <a
            href="https://wa.me/919962610113?text=Hi!%20I%20came%20across%20GD%20Brand%20Creative%20on%20Travel%20Rethink%20Ways%20and%20I'd%20like%20to%20enquire%20about%20your%20creative%20services%20for%20my%20business."
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Contact GD Brand Creative for creative services"
            className="block w-full overflow-hidden rounded-[10px]"
          >
            <img
              src={gdAdMobile}
              alt="GD Brand Creative mobile banner"
              className="w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </a>
        </div>
        
      </div>

      <main className="min-h-screen">
        <Suspense fallback={null}>
          <Routes>
          <Route path="/privacy-policy"       element={<Navigate to="/privacy-policy/" replace />} />
          <Route path="/privacy-policy/"      element={<PrivacyPolicy />} />
          <Route path="/cookies-policy"       element={<Navigate to="/cookies-policy/" replace />} />
          <Route path="/cookies-policy/"      element={<CookiesPolicy />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms-and-conditions/" replace />} />
          <Route path="/terms-and-conditions/" element={<TermsAndConditions />} />
          <Route path="/affiliate-disclosure" element={<Navigate to="/affiliate-disclosure/" replace />} />
          <Route path="/affiliate-disclosure/" element={<AffiliateDisclosure />} />
          <Route path="/super-deals"          element={<Navigate to="/super-deals/" replace />} />
          <Route path="/super-deals/"         element={<SuperDeals darkMode={darkMode} />} />
          <Route path="/about-us"             element={<Navigate to="/about-us-travel-rethink-ways/" replace />} />
          <Route path="/about-us-travel-rethink-ways" element={<Navigate to="/about-us-travel-rethink-ways/" replace />} />
          <Route path="/about-us-travel-rethink-ways/" element={<AboutUs />} />
          <Route path="/contact-us"           element={<Navigate to="/contact-us/" replace />} />
          <Route path="/contact-us/"           element={<ContactUs />} />
          <Route path="/super-deals-manager"  element={<SuperDealsManager />} />
          <Route path="/disclaimer"           element={<Navigate to="/disclaimer-travel-rethink-ways/" replace />} />
          <Route path="/disclaimer-travel-rethink-ways" element={<Navigate to="/disclaimer-travel-rethink-ways/" replace />} />
          <Route path="/disclaimer-travel-rethink-ways/" element={<Disclaimer />} />
          <Route path="/tour-packages"        element={<Navigate to="/travel-tour-packages/" replace />} />
          <Route path="/travel-tour-packages" element={<Navigate to="/travel-tour-packages/" replace />} />
          <Route path="/travel-tour-packages/" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/tour-packages/:destinationSlug-tour-packages" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/:destinationSlug-tour-packages" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/travel-web-stories"   element={<Navigate to="/travel-web-stories/" replace />} />
          <Route path="/travel-web-stories/"  element={<WebStory />} />
          <Route path="/visual-stories"       element={<Navigate to="/travel-web-stories/" replace />} />
          <Route path="/wishlist"             element={<Navigate to="/wishlist-favorites/" replace />} />
          <Route path="/wishlist-favorites"   element={<Navigate to="/wishlist-favorites/" replace />} />
          <Route path="/wishlist-favorites/"  element={<Wishlistfavorites darkMode={darkMode} />} />
			      <Route path="/latest-updates"       element={<CategoryPage />} />
			      <Route path="/search"               element={<SearchResults darkMode={darkMode} />} />
			      <Route path="/category"             element={<CategoryPage />} />
			      <Route path="/category/:slug"       element={<CategoryPage />} />
          <Route path="/advertise-with-us"      element={<Navigate to="/advertise-with-us/" replace />} />
          <Route path="/advertise-with-us/"     element={<AdvertiseWithUs />} />
          <Route path="/jobs/*"              element={<Jobs darkMode={darkMode} />} />
          <Route path="/"                     element={isLegacySearchPage ? <SearchResults darkMode={darkMode} /> : <Home darkMode={darkMode} />} />
          </Routes>
        </Suspense>
      </main>

      {isKeywordAllowedPage && (
        <>
          <Keywords />
          <BigKeywords />
        </>
      )}

      <Footer />
    </div>
  );
}

export default App;
